import { IconButton, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { SingleDatePickerController, useClientOptions, useDatePickerStyles } from '@paintscout/ui-business'
import classnames from 'classnames'
import {
  expiryEnabled,
  getExpiryDate,
  getObjectLabels,
  setDate,
  setExpiryDate,
  toggleExpiry
} from '@paintscout/util/builder'
import EventIcon from '@material-ui/icons/EventOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useQuote } from '../../context'

type Section = 'issued' | 'expires'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        flexDirection: 'column',
        // Gap between the Issued/Expires cards matches the popover's (halved) side padding.
        gap: theme.spacing(0.75),
        minWidth: 300
      },
      // Each section (Issued / Expires) is a bordered rounded card; when expanded it also holds the
      // inline calendar (matching mocks #24/#25).
      card: {
        border: `1px solid ${neutral.P90}`,
        borderRadius: 8,
        backgroundColor: neutral.White,
        overflow: 'hidden'
      },
      header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5)
      },
      // The clickable title (icon + "Issued: <date>") — resets native <button> chrome.
      headerButton: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1,
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        padding: 0,
        font: 'inherit',
        cursor: 'pointer',
        textAlign: 'left'
      },
      headerIcon: {
        fontSize: 16,
        color: neutral.P60,
        flexShrink: 0
      },
      headerText: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: neutral.P40,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      eyeButton: {
        padding: theme.spacing(0.5),
        color: neutral.P60,
        flexShrink: 0
      },
      eyeIcon: {
        fontSize: 18
      },
      // Applied alongside the shared picker's `.popper` class (so its descendant calendar styles —
      // caption padding, edge-positioned month arrows, blue selected day, hidden "?" button — kick
      // in), but stripped of the popper's own chrome (drop shadow / margin) so it sits flush in the
      // card. No divider between the title row and the calendar.
      calendar: {
        boxShadow: 'none !important',
        margin: '0 !important'
      }
    }
  },
  { name: 'QuoteNextDateEditor' }
)

const formatDate = (value?: number | null) => (value ? moment(value).format('MMMM D, YYYY') : '—')

/**
 * The redesigned Issued / Expiry editor shown inside the estimateNext header's date pop-over. Two
 * collapsible bordered cards (accordion — one open at a time): each shows a title row ("Issued: …" /
 * "Expires: …") and, when expanded, the shared inline calendar (SingleDatePickerController). The
 * Expires card carries an eye toggle to enable/disable the expiry. Persists via the shared builders
 * (setDate / setExpiryDate / toggleExpiry) through the quote context.
 */
export default function QuoteNextDateEditor() {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { quote, updateQuote } = useQuote()
  const objectLabels = getObjectLabels({ options, invoice: quote?.is_invoice })

  // The shared date-picker styles are scoped under the `.popper` class — wrap the inline calendar in
  // it so its caption padding, edge-positioned month arrows, blue selected day, etc. apply. The
  // transition-container height is driven by the measured visible-month height (like HeaderCardDatePicker).
  const [tableHeight, setTableHeight] = useState(0)
  const dpClasses = useDatePickerStyles({ isSingle: true, tableHeight })
  const recalculateTableHeight = () => {
    const els = document.getElementsByClassName('CalendarMonthGrid_month__horizontal')
    if (els.length > 0) {
      setTableHeight((els[0] as HTMLElement).offsetHeight)
    }
  }

  const expiryField = quote?.is_invoice ? 'customerInvoiceExpiry' : 'customerViewExpiry'

  const settingsExpiryEnabled = !!options.options?.quotes?.[expiryField]?.enabled
  const enabledExpiry = expiryEnabled({ quote, options })
  const expiryDate = getExpiryDate({ quote })
  const expiryLabel = quote?.is_invoice ? 'Due' : 'Expires'

  // Accordion: default to the Issued calendar open (mock #24).
  const [expanded, setExpanded] = useState<Section>('issued')

  // Re-measure the calendar height whenever the open section changes (only one calendar is mounted).
  useEffect(() => {
    recalculateTableHeight()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])

  const handleIssuedChange = (date: moment.Moment | null) => {
    if (date) {
      updateQuote({ quote: setDate({ quote, date: date.unix() * 1000 }) })
    }
  }

  const handleExpiryChange = (date: moment.Moment | null) => {
    const nextExpiry = date ? date.endOf('day').unix() * 1000 : null
    let updated = setExpiryDate({ quote, expiryDate: nextExpiry })
    // Choosing a date implies the expiry should be on — enable it if it wasn't already.
    if (nextExpiry && !expiryEnabled({ quote, options })) {
      updated = toggleExpiry({ quote: updated, enabled: true })
    }
    updateQuote({ quote: updated })
  }

  const handleToggleExpiry = () => {
    const next = !enabledExpiry
    updateQuote({ quote: toggleExpiry({ quote, enabled: next }) })
    if (next) {
      setExpanded('expires')
    } else if (expanded === 'expires') {
      setExpanded('issued')
    }
  }

  return (
    <div className={classes.root}>
      {/* Issued */}
      <div className={classes.card}>
        <div className={classes.header}>
          <button type="button" className={classes.headerButton} onClick={() => setExpanded('issued')}>
            <EventIcon className={classes.headerIcon} />
            <Typography component="span" className={classes.headerText}>
              Created: {formatDate(quote?.quote_date)}
            </Typography>
          </button>
        </div>
        {expanded === 'issued' && (
          <div className={classnames(dpClasses.popper, classes.calendar)}>
            <SingleDatePickerController
              date={moment(quote?.quote_date)}
              onChange={handleIssuedChange}
              recalculateTableHeight={recalculateTableHeight}
            />
          </div>
        )}
      </div>

      {/* Expires — only when the expiry feature is enabled in settings. */}
      {settingsExpiryEnabled && (
        <div className={classes.card}>
          <div className={classes.header}>
            <button
              type="button"
              className={classes.headerButton}
              onClick={() => enabledExpiry && setExpanded('expires')}
            >
              <EventIcon className={classes.headerIcon} />
              <Typography component="span" className={classes.headerText}>
                {expiryLabel}: {enabledExpiry ? formatDate(expiryDate) : `Off`}
              </Typography>
            </button>
            <IconButton
              className={classes.eyeButton}
              size="small"
              aria-label={
                enabledExpiry
                  ? `Disable ${objectLabels.quote.value} expiry`
                  : `Enable ${objectLabels.quote.value} expiry`
              }
              onClick={handleToggleExpiry}
            >
              {enabledExpiry ? (
                <VisibilityOffIcon className={classes.eyeIcon} />
              ) : (
                <VisibilityIcon className={classes.eyeIcon} />
              )}
            </IconButton>
          </div>
          {enabledExpiry && expanded === 'expires' && (
            <div className={classnames(dpClasses.popper, classes.calendar)}>
              <SingleDatePickerController
                date={expiryDate ? moment(expiryDate) : null}
                onChange={handleExpiryChange}
                recalculateTableHeight={recalculateTableHeight}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
